import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ClientRecord } from "../../ClientsGrid.utils";
import styles from "./ClientActionsRenderer.module.css";

interface ClientActionsRendererProps {
  data: ClientRecord;
  setAddNewWizardShow: (arg: boolean) => void;
  setClientMasterId: (arg: number) => void;
  setClientName: (arg: string) => void;
  setClientId: (arg: string) => void;
  setAuditTrailModalShow: (arg: boolean) => void;
  setApprovalPdfViewerShow: (arg: boolean) => void;
  setClientDetailsPdfUrl: (arg: string) => void; // Pass URL directly to modal
}

const ClientActionsRenderer: React.FC<ClientActionsRendererProps> = ({
  data,
  setAddNewWizardShow,
  setClientMasterId,
  setClientName,
  setClientId,
  setAuditTrailModalShow,
  setApprovalPdfViewerShow,
  setClientDetailsPdfUrl,
}) => {
  if (!data) return null;

  const engage = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    setAddNewWizardShow(true);
  };

  const openAuditTrail = () => {
    setClientMasterId(data.ClientMasterId);
    setClientId(data.ClientId);
    setClientName(data["Client Name"]);
    setAuditTrailModalShow(true);
  };

  // Open approval PDF viewer - now uses URL directly from grid data
  const openApprovalPdfViewer = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    setClientDetailsPdfUrl(data.ClientDetailsPdfUrl || "");
    setApprovalPdfViewerShow(true);
  };

  const isApproved = data["Approval Status"].toLowerCase().includes("approved");

  const isEngagementLetterPending =
    data.EngagementLetterStatus === "pending" ||
    data.EngagementLetterStatus === undefined;

  const engagementButtonAction = isEngagementLetterPending ? engage : undefined;
  const engagementButtonDisabled = !isApproved;
  const engagementTooltipMessage = engagementButtonDisabled
    ? "Client approval is pending."
    : isEngagementLetterPending
    ? "Create Engagement Letter"
    : "Engagement letter already exists. Click to create another.";

  // Check if ClientDetailsPdfUrl exists to determine if button should be shown
  const hasApprovalPdf = !!(data.ClientDetailsPdfUrl && data.ClientDetailsPdfUrl.trim() !== "");

  return (
    <>
      {/* Engagement Letter button */}
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">{engagementTooltipMessage}</Tooltip>
        }
      >
        <span className={styles.tooltipWrapper}>
          <Button
            className={styles.actionButton}
            onClick={engagementButtonAction}
            disabled={engagementButtonDisabled}
          >
            <Icon.FileEarmarkRichtext />
          </Button>
        </span>
      </OverlayTrigger>

      {/* View Approval PDF button - Only render if ClientDetailsPdfUrl exists */}
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={
            <Tooltip id="button-tooltip">{hasApprovalPdf ? "View Approval Document" : "No Approval Document Available"}</Tooltip>
          }
        >
          <span className={styles.tooltipWrapper}>
            <Button
              className={styles.actionButton}
              disabled={!hasApprovalPdf}
              onClick={openApprovalPdfViewer}
            >
              <Icon.Eye />
            </Button>
          </span>
        </OverlayTrigger>
      

      {/* Audit Trail button */}
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="tooltip-audit">View Audit Trail</Tooltip>}
      >
        <span className={styles.tooltipWrapper}>
          <Button className={styles.auditActionButton} onClick={openAuditTrail}>
            <Icon.ClockHistory />
          </Button>
        </span>
      </OverlayTrigger>
    </>
  );
};

export default ClientActionsRenderer;